import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { clientsApi } from '../lib/api';

interface SectionProps {
  title: string;
  description: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  badge?: string;
}

function CollapsibleSection({ title, description, isOpen, onToggle, children, badge }: SectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <span className={`transform transition-transform ${isOpen ? 'rotate-90' : ''}`}>
            &#9654;
          </span>
          <div className="text-left">
            <h3 className="font-medium text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
        {badge && (
          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
            {badge}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="px-4 py-4 space-y-4 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
}

export function AddClientPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Section toggles
  const [openSections, setOpenSections] = useState({
    basic: true,
    wordpress: false,
    youtube: false,
    podbean: false,
    google: false,
    renderform: false,
    facebook: false,
    gbp: false,
  });

  // Basic client info
  const [formData, setFormData] = useState({
    companyName: '',
    slug: '',
    websiteUrl: '',
    city: '',
    niche: '',
    napName: '',
    napPhone: '',
    napAddress: '',
    logoUrl: '',
    gmbUrl: '',
  });

  // Credentials
  const [credentials, setCredentials] = useState({
    // WordPress
    wordpressBaseUrl: '',
    wordpressUser: '',
    wordpressAppPassword: '',
    wordpressDefaultCategory: '',
    // YouTube
    youtubeClientId: '',
    youtubeClientSecret: '',
    youtubeRefreshToken: '',
    youtubePrivacyStatus: 'public',
    // Podbean
    podbeanClientId: '',
    podbeanClientSecret: '',
    podbeanPodcastId: '',
    // Google
    sheetsSpreadsheetId: '',
    sheetsTabName: '',
    driveParentFolderId: '',
    // RenderForm
    renderformTemplateId: '',
    renderformTextFieldName: '',
    renderformQrFieldName: '',
    // Facebook
    facebookPageId: '',
    facebookAccessToken: '',
    // GBP
    gbpAccountId: '',
    gbpLocationId: '',
    gbpAccessToken: '',
    gbpRefreshToken: '',
  });

  function toggleSection(section: keyof typeof openSections) {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  }

  function generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'companyName') {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  }

  function handleCredentialChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.companyName || !formData.slug || !formData.websiteUrl || !formData.city || !formData.niche) {
        throw new Error('Please fill in all required fields (Company Name, Slug, Website, City, Niche)');
      }

      // Validate slug format
      if (!/^[a-z0-9-]+$/.test(formData.slug)) {
        throw new Error('Slug must contain only lowercase letters, numbers, and hyphens');
      }

      // Validate URL
      try {
        new URL(formData.websiteUrl);
      } catch {
        throw new Error('Please enter a valid website URL');
      }

      // Create client
      const clientPayload: Record<string, string> = {
        companyName: formData.companyName,
        slug: formData.slug,
        websiteUrl: formData.websiteUrl,
        city: formData.city,
        niche: formData.niche,
      };

      if (formData.napName) clientPayload.napName = formData.napName;
      if (formData.napPhone) clientPayload.napPhone = formData.napPhone;
      if (formData.napAddress) clientPayload.napAddress = formData.napAddress;
      if (formData.logoUrl) clientPayload.logoUrl = formData.logoUrl;
      if (formData.gmbUrl) clientPayload.gmbUrl = formData.gmbUrl;

      await clientsApi.create(clientPayload);

      // Save credentials if any were provided
      const hasCredentials = Object.values(credentials).some(v => v && v.trim());
      if (hasCredentials) {
        const credPayload: Record<string, string | number> = {};

        // Only include non-empty values
        if (credentials.wordpressBaseUrl) credPayload.wordpressBaseUrl = credentials.wordpressBaseUrl;
        if (credentials.wordpressUser) credPayload.wordpressUser = credentials.wordpressUser;
        if (credentials.wordpressAppPassword) credPayload.wordpressAppPassword = credentials.wordpressAppPassword;
        if (credentials.wordpressDefaultCategory) credPayload.wordpressDefaultCategory = parseInt(credentials.wordpressDefaultCategory);

        if (credentials.youtubeClientId) credPayload.youtubeClientId = credentials.youtubeClientId;
        if (credentials.youtubeClientSecret) credPayload.youtubeClientSecret = credentials.youtubeClientSecret;
        if (credentials.youtubeRefreshToken) credPayload.youtubeRefreshToken = credentials.youtubeRefreshToken;
        if (credentials.youtubePrivacyStatus) credPayload.youtubePrivacyStatus = credentials.youtubePrivacyStatus;

        if (credentials.podbeanClientId) credPayload.podbeanClientId = credentials.podbeanClientId;
        if (credentials.podbeanClientSecret) credPayload.podbeanClientSecret = credentials.podbeanClientSecret;
        if (credentials.podbeanPodcastId) credPayload.podbeanPodcastId = credentials.podbeanPodcastId;

        if (credentials.sheetsSpreadsheetId) credPayload.sheetsSpreadsheetId = credentials.sheetsSpreadsheetId;
        if (credentials.sheetsTabName) credPayload.sheetsTabName = credentials.sheetsTabName;
        if (credentials.driveParentFolderId) credPayload.driveParentFolderId = credentials.driveParentFolderId;

        if (credentials.renderformTemplateId) credPayload.renderformTemplateId = credentials.renderformTemplateId;
        if (credentials.renderformTextFieldName) credPayload.renderformTextFieldName = credentials.renderformTextFieldName;
        if (credentials.renderformQrFieldName) credPayload.renderformQrFieldName = credentials.renderformQrFieldName;

        if (credentials.facebookPageId) credPayload.facebookPageId = credentials.facebookPageId;
        if (credentials.facebookAccessToken) credPayload.facebookAccessToken = credentials.facebookAccessToken;

        if (credentials.gbpAccountId) credPayload.gbpAccountId = credentials.gbpAccountId;
        if (credentials.gbpLocationId) credPayload.gbpLocationId = credentials.gbpLocationId;
        if (credentials.gbpAccessToken) credPayload.gbpAccessToken = credentials.gbpAccessToken;
        if (credentials.gbpRefreshToken) credPayload.gbpRefreshToken = credentials.gbpRefreshToken;

        await clientsApi.updateCredentials(formData.slug, credPayload);
      }

      navigate(`/clients/${formData.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <Link to="/clients" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Clients
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Add New Client</h1>
          <p className="text-sm text-gray-500 mt-1">Fill in the basic information and configure API integrations</p>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Basic Information */}
          <CollapsibleSection
            title="Basic Information"
            description="Company details and NAP information"
            isOpen={openSections.basic}
            onToggle={() => toggleSection('basic')}
            badge="Required"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g. Piedmont Dental"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g. piedmont-dental"
                  pattern="[a-z0-9-]+"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Auto-generated from company name</p>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="https://example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g. Rock Hill"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Niche <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="niche"
                  value={formData.niche}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g. Dentist"
                  required
                />
              </div>

              <div className="col-span-2 border-t pt-4 mt-2">
                <p className="text-sm font-medium text-gray-700 mb-3">NAP Information (Optional)</p>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                <input
                  type="text"
                  name="napName"
                  value={formData.napName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Full business name for NAP"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  name="napPhone"
                  value={formData.napPhone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                <input
                  type="url"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  name="napAddress"
                  value={formData.napAddress}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Google Business Profile URL</label>
                <input
                  type="url"
                  name="gmbUrl"
                  value={formData.gmbUrl}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="https://g.page/..."
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* WordPress */}
          <CollapsibleSection
            title="WordPress API"
            description="Blog posting credentials"
            isOpen={openSections.wordpress}
            onToggle={() => toggleSection('wordpress')}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">WordPress Base URL</label>
                <input
                  type="url"
                  name="wordpressBaseUrl"
                  value={credentials.wordpressBaseUrl}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  name="wordpressUser"
                  value={credentials.wordpressUser}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="admin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Password</label>
                <input
                  type="password"
                  name="wordpressAppPassword"
                  value={credentials.wordpressAppPassword}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="xxxx xxxx xxxx xxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Category ID</label>
                <input
                  type="number"
                  name="wordpressDefaultCategory"
                  value={credentials.wordpressDefaultCategory}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* YouTube */}
          <CollapsibleSection
            title="YouTube API"
            description="Video upload credentials (OAuth 2.0)"
            isOpen={openSections.youtube}
            onToggle={() => toggleSection('youtube')}
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                <input
                  type="text"
                  name="youtubeClientId"
                  value={credentials.youtubeClientId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="xxxxx.apps.googleusercontent.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                <input
                  type="password"
                  name="youtubeClientSecret"
                  value={credentials.youtubeClientSecret}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="GOCSPX-xxxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Token</label>
                <input
                  type="password"
                  name="youtubeRefreshToken"
                  value={credentials.youtubeRefreshToken}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1//xxxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Privacy Status</label>
                <select
                  name="youtubePrivacyStatus"
                  value={credentials.youtubePrivacyStatus}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
          </CollapsibleSection>

          {/* Podbean */}
          <CollapsibleSection
            title="Podbean API"
            description="Podcast upload credentials"
            isOpen={openSections.podbean}
            onToggle={() => toggleSection('podbean')}
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                <input
                  type="text"
                  name="podbeanClientId"
                  value={credentials.podbeanClientId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="aabdf8aa..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                <input
                  type="password"
                  name="podbeanClientSecret"
                  value={credentials.podbeanClientSecret}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="xxxxxxxx"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Podcast ID</label>
                <input
                  type="text"
                  name="podbeanPodcastId"
                  value={credentials.podbeanPodcastId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="8Rer2as2NIOG"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Google (Sheets & Drive) */}
          <CollapsibleSection
            title="Google Sheets & Drive"
            description="Spreadsheet tracking and file storage"
            isOpen={openSections.google}
            onToggle={() => toggleSection('google')}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Spreadsheet ID</label>
                <input
                  type="text"
                  name="sheetsSpreadsheetId"
                  value={credentials.sheetsSpreadsheetId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tab Name</label>
                <input
                  type="text"
                  name="sheetsTabName"
                  value={credentials.sheetsTabName}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Sheet1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Drive Parent Folder ID</label>
                <input
                  type="text"
                  name="driveParentFolderId"
                  value={credentials.driveParentFolderId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* RenderForm */}
          <CollapsibleSection
            title="RenderForm"
            description="Hero image generation"
            isOpen={openSections.renderform}
            onToggle={() => toggleSection('renderform')}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Template ID</label>
                <input
                  type="text"
                  name="renderformTemplateId"
                  value={credentials.renderformTemplateId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="template-id"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Field Name</label>
                <input
                  type="text"
                  name="renderformTextFieldName"
                  value={credentials.renderformTextFieldName}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">QR Field Name</label>
                <input
                  type="text"
                  name="renderformQrFieldName"
                  value={credentials.renderformQrFieldName}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="qr_code"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Facebook */}
          <CollapsibleSection
            title="Facebook"
            description="Page posting credentials"
            isOpen={openSections.facebook}
            onToggle={() => toggleSection('facebook')}
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page ID</label>
                <input
                  type="text"
                  name="facebookPageId"
                  value={credentials.facebookPageId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                <input
                  type="password"
                  name="facebookAccessToken"
                  value={credentials.facebookAccessToken}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="EAAxxxxx..."
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Google Business Profile */}
          <CollapsibleSection
            title="Google Business Profile (GBP)"
            description="Local business post credentials - if not configured, workflow will pause for manual posting"
            isOpen={openSections.gbp}
            onToggle={() => toggleSection('gbp')}
          >
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> If GBP credentials are not configured, the workflow will complete all other steps and then pause, providing you with the blog URL, hero image, and suggested post content for manual GBP posting.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account ID</label>
                <input
                  type="text"
                  name="gbpAccountId"
                  value={credentials.gbpAccountId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="accounts/123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location ID</label>
                <input
                  type="text"
                  name="gbpLocationId"
                  value={credentials.gbpLocationId}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="locations/987654321"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                <input
                  type="password"
                  name="gbpAccessToken"
                  value={credentials.gbpAccessToken}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="ya29.xxxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Token</label>
                <input
                  type="password"
                  name="gbpRefreshToken"
                  value={credentials.gbpRefreshToken}
                  onChange={handleCredentialChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1//xxxxx"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <Link
              to="/clients"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
